class DialogComponent extends HTMLElement {
  constructor() {
    super();

    /**
     * @type {HTMLDialogElement}
     */
    this.dialog = this.querySelector("dialog");
  }

  showDialog() {
    if (this.dialog.open) return;

    requestAnimationFrame(() => {
      this.dialog.showModal();

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener("click", this.#handleClick);
      this.addEventListener("keydown", this.#handleKeyDown);
    });
  }

  async closeDialog() {
    if (!this.dialog.open) return;

    this.removeEventListener("click", this.#handleClick);
    this.removeEventListener("keydown", this.#handleKeyDown);

    this.dialog.style.animation = "none";

    // Trigger a reflow to apply the animation reset before adding the closing class
    void this.dialog.offsetWidth;

    this.dialog.classList.add("closing");

    this.dialog.style.animation = "";

    await this.onAnimationEnd(this.dialog, undefined);

    this.dialog.close();
    this.dialog.classList.remove("closing");

    this.dispatchEvent(new DialogCloseEvent());
  }

  // TODO: Move this to a utility file if we need it elsewhere
  onAnimationEnd(elements, callback, options = { subtree: true }) {
    const animations = Array.isArray(elements)
      ? elements.flatMap((element) => element.getAnimations(options))
      : elements.getAnimations(options);

    const animationPromises = animations.reduce((acc, animation) => {
      // Ignore ViewTimeline animations
      if (animation.timeline instanceof DocumentTimeline) {
        acc.push(animation.finished);
      }

      return acc;
    }, []);

    return Promise.allSettled(animationPromises).then(callback);
  }

  toggleDialog() {
    if (this.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  }

  #handleClick(event) {
    if (
      event.target === this.dialog ||
      event.target.closest("[data-dialog-close]")
    ) {
      this.closeDialog();
    }
  }

  #handleKeyDown(event) {
    if (event.key !== "Escape") return;

    event.preventDefault();
    this.closeDialog();
  }
}

customElements.define("dialog-component", DialogComponent);

class DialogTrigger extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector("button");
    this.boundHandleClick = this.#handleClick.bind(this);
  }

  connectedCallback() {
    this.button.addEventListener("click", this.boundHandleClick);
  }

  disconnectedCallback() {
    this.button.removeEventListener("click", this.boundHandleClick);
  }

  #handleClick() {
    const dialogId = this.button.getAttribute("data-dialog-target");
    const dialog = document.getElementById(dialogId);

    if (dialog) {
      dialog.showDialog();
    }
  }
}

customElements.define("dialog-trigger", DialogTrigger);

class DialogOpenEvent extends CustomEvent {
  constructor() {
    super("dialog:open");
  }
}

class DialogCloseEvent extends CustomEvent {
  constructor() {
    super("dialog:close");
  }
}

document.addEventListener("DOMContentLoaded", (event) => {
  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-dialog-target]");

    if (!trigger) return;

    const dialogId = trigger.getAttribute("data-dialog-target");
    const dialog = document.getElementById(dialogId);

    if (dialog) {
      dialog.showDialog();
    }
  });
});
